import React from 'react'
import { ICourse, ICourseTieringPrices } from '../../interfaces/ICourse'
import { GetCouponRequest } from '../../services/admin-service/api-request'
import PayWithPaymentProviderButton from './PayWithProviderButton'
import { trackFbPixelsPurchase } from '../../lib/analytics/fbPixels'
import { useTheme } from '@mui/material'

import Button from '@mui/material/Button'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuList from '@mui/material/MenuList'
import HubspotLoanApplicationFormDialogButton from '../SchoolDetailsComponents/HubspotLoanApplicationFormDialogButton'
import { enableLoanInSchool, isWhitelabelSchool } from '@/services/utils'
import { WhitelabelCoursePaymentButton } from '../Whitelabel/WhitelabelSchool/WhitelabelPaymentButton'

interface IPaymentForCourseButtonProps {
  courseDetails: ICourse
  school: any
  user_details: any
  onCloseDialog?: () => void
  buttonVariant?: 'contained' | 'outlined' | 'text'
  fullWidth?: boolean
  buttonSize?: 'large' | 'small' | 'medium'
  buttonStyle?: any
  buttonDisableElevation?: boolean
  openDialogByDefault?: boolean
}

const PayforCourseTierComponent = (
  props: IPaymentForCourseButtonProps & { courseTier: ICourseTieringPrices },
) => {
  const theme = useTheme()
  function closeDialogFunc() {
    props.onCloseDialog && props.onCloseDialog()
  }

  function onTransactionComplete() {
    trackFbPixelsPurchase(props.school, props.courseDetails?.name)
    props.onCloseDialog && props.onCloseDialog()
  }

  return (
    <PayWithPaymentProviderButton
      buttonDisableElevation={props.buttonDisableElevation}
      fullWidth={props.fullWidth}
      getCoupon={GetCouponRequest}
      buttonVariant={props.buttonVariant}
      userDetails={props.user_details}
      school_details={props.school}
      courseId={props.courseDetails._id}
      amount={props.courseTier?.price}
      productName={`Partial payment for tier ${props.courseTier?.tiering}, Course: ${props.courseDetails?.name}, School: ${props.school.name}`}
      paymentFor={'school_fees'}
      setTransactionComplete={closeDialogFunc}
      onTransactionComplete={onTransactionComplete}
      buttonStyle={{
        borderRadius: '0px',
        ...props.buttonStyle,
        color: theme.palette.text.primary,
        fontSize: '80%',
      }}
      openDialogByDefault={props.openDialogByDefault}
      useMenuItemComponents={true}
      courseTier={props.courseTier?.tiering}
    />
  )
}

export default function PaymentForCourseButton(
  props: IPaymentForCourseButtonProps,
) {
  const [openMenu, setOpenMenu] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const [priceTiering] = React.useState(
    props.courseDetails.tiering_prices.filter(t => t?.price > 0),
  )
  const [allowLoan] = React.useState(enableLoanInSchool(props.school))
  const [allowTiering] = React.useState(
    props.courseDetails?.enable_tiering && priceTiering?.length > 0,
  )
  const [showOtherPaymentOptions] = React.useState(allowLoan || allowTiering)
  const [notWhitelabelSchool] = React.useState(
    !isWhitelabelSchool(props.school),
  )

  function closeDialogFunc() {
    props.onCloseDialog && props.onCloseDialog()
  }

  function onTransactionComplete() {
    trackFbPixelsPurchase(props.school, props.courseDetails?.name)
    props.onCloseDialog && props.onCloseDialog()
  }

  const handleToggle = () => {
    setOpenMenu(prevOpen => !prevOpen)
  }

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }

    setOpenMenu(false)
  }

  return (
    <div>
      <div
        ref={anchorRef}
        style={{ display: 'flex', margin: '-5px', justifyContent: 'center' }}
      >
        {!notWhitelabelSchool && (
          <WhitelabelCoursePaymentButton
            courseDetails={props.courseDetails}
            school={props.school}
          />
        )}

        {notWhitelabelSchool && (
          <PayWithPaymentProviderButton
            buttonDisableElevation={props.buttonDisableElevation}
            fullWidth={props.fullWidth}
            getCoupon={GetCouponRequest}
            buttonVariant={props.buttonVariant}
            userDetails={props.user_details}
            school_details={props.school}
            courseId={props.courseDetails._id}
            amount={props.courseDetails.price}
            productName={`Payment for course: ${props.courseDetails.name} in school: ${props.school.name}`}
            paymentFor={'school_fees'}
            setTransactionComplete={closeDialogFunc}
            onTransactionComplete={onTransactionComplete}
            buttonStyle={{ borderRadius: '0px', ...props.buttonStyle }}
            openDialogByDefault={props.openDialogByDefault}
          />
        )}
        {showOtherPaymentOptions && notWhitelabelSchool && (
          <>
            <Button
              size="large"
              disableElevation={props.buttonDisableElevation}
              aria-controls={openMenu ? 'course-tier-pricing' : undefined}
              aria-expanded={openMenu ? 'true' : undefined}
              aria-label="select course tier to pay for"
              aria-haspopup="menu"
              variant="contained"
              color="primary"
              style={{
                borderRadius: '0px',
                margin: 'auto',
                marginLeft: '2px',
                marginRight: '2px',
                height: '42px',
                ...props.buttonStyle,
                width: '20px',
              }}
              onClick={handleToggle}
            >
              <ArrowDropDownIcon />
            </Button>
          </>
        )}
      </div>
      <Popper
        sx={{
          zIndex: 1,
        }}
        open={openMenu}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              {/* {allowTiering && <Typography align="center" variant="body2" style={{ paddingTop: "15px" }}><u>Partial access payment</u></Typography>} */}
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="course tiers" autoFocusItem>
                  {allowTiering &&
                    priceTiering.map(courseTier => {
                      return (
                        <PayforCourseTierComponent
                          {...props}
                          courseTier={courseTier}
                        />
                      )
                    })}
                  {allowLoan && (
                    <div style={{ padding: '5px 10px' }}>
                      <HubspotLoanApplicationFormDialogButton
                        buttonSize="small"
                        buttonVariant="text"
                        buttonText="Apply for a loan"
                      />
                    </div>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}
